import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import { catchError, filter, timeout } from 'rxjs/operators';
import { AIResult, AIService } from 'src/app/ai/ai.service';
import { CompanyState } from 'src/app/companies/company.state';

import { Partner } from '../../models/Partner';
import { VAT } from '../../models/VAT';
import { ContactMapper } from '../../models/yuki/ContactMapper';
import YukiDocument from '../../models/yuki/YukiDocument';
import { YukiFile } from '../../models/yuki/YukiFile';
import { PartnerAddModalComponent } from '../../partners/partner-add-modal/partner-add-modal.component';
import { FetchPartners } from '../../partners/partners.actions';
import { PartnerState } from '../../partners/partners.state';
import { AddChildLinkConfirmFromTimesheetsModalComponent } from '../../shared/global-files/add-child-link-confirm-from-timesheets-modal/add-child-link-confirm-from-timesheets-modal.component';
import { ChildInvoiceLinkDTO } from '../../shared/global-files/add-child-link-modal/add-child-link-modal.component';
import { AddChildLinkModalComponent } from '../../shared/global-files/add-child-link-modal/add-child-link-modal.component';
import { AddSubLinkToLinkedObjectToFile, GetAllObjectsLinkedToFile, GetAllObjectsToLink } from '../../shared/global-files/global-files.actions';
import { GlobalFilesState } from '../../shared/global-files/global-files.state';
import { roundNumber } from '../../shared/utils/number-utils';
import { YukiDocumentDTO } from '../yuki-detail-modal/yuki-detail-modal.component';
import { FetchFile, UpdateVATOfYukiDocument } from '../yuki.actions';
import { GetMappedContacts } from '../yuki.actions';
import { YukiState } from '../yuki.state';

@Component({
    selector: 'app-yuki-update-information',
    templateUrl: './yuki-update-information.component.html',
    styleUrls: ['./yuki-update-information.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class YukiUpdateInformationComponent implements OnInit, OnChanges {
    VATForm: UntypedFormGroup;
    VATs: VAT[] = [];
    @Input() yukiDocument: YukiDocument;
    @Output() emitDocumentVerified = new EventEmitter<void>();

    @Select(PartnerState.getPartners) partners$: Observable<Partner[]>;
    partners: Partner[];
    @Select(YukiState.mappedContacts) mappedContacts$: Observable<ContactMapper[]>;
    mappedContacts: ContactMapper[] = [];
    private lastAutoSelectedDocumentUuid: string | null = null;

    private subscriptions = new Subscription();
    private documentSubscription: Subscription | null = null;
    private subscribedDocumentUuid: string | null = null;
    aiLoading = false;

    // File uit NGXS (zelfde bron als YukiFileComponent)
    @Select(YukiState.file) file$: Observable<YukiFile>;
    private currentFile: YukiFile | null = null;
    @Select(CompanyState.companyUuid) companyUuid$: Observable<string>;
    companyUuid: string | null = null;
    companyLogbookPrompt: string | null = null;

    constructor(
        private fb: UntypedFormBuilder,
        private store: Store,
        private logger: NGXLogger,
        private toastr: ToastrService,
        private modalService: NgbModal,
        private aiService: AIService,
    ) {}

    ngOnInit(): void {
        this.createVATForm();
        this.seedVATs();
        this.fetchData();

        // load company logbook prompt when company changes
        this.subscriptions.add(
            this.companyUuid$?.subscribe(async (id) => {
                this.companyUuid = id || null;
                await this.loadCompanyLogbookPrompt();
            }),
        );

        // Houd laatste file bij
        this.subscriptions.add(
            this.file$.subscribe((file) => {
                this.currentFile = file || null;
            }),
        );
    }

    ngOnChanges() {
        this.createVATForm();
        this.resubscribeToDocument();
    }

    updateVATAmount() {
        if (this.VATForm.value.vat === VAT.OTHER) return;
        const amount: number = this.VATForm.value.amount;
        const percent: number = this.VATForm.value.vat.percent;
        const vat: number = amount - (amount * 100) / (percent + 100);
        const amountExcl: number = amount - vat;
        this.VATForm.get('vatAmount').setValue(`${vat.toFixed(2)}`);
        this.VATForm.get('amountExcl').setValue(`${amountExcl.toFixed(2)}`);
    }

    checkAmountError(): boolean {
        const amount: number = parseFloat(this.VATForm.value.amount);
        const amountExcl: number = parseFloat(this.VATForm.value.amountExcl);
        const vatAmount: number = parseFloat(this.VATForm.value.vatAmount);
        return (vatAmount + amountExcl).toFixed(2) !== amount.toFixed(2);
    }

    updateDocument() {
        if (!this.yukiDocument?.uuid) {
            this.toastr.error('Document is not loaded yet.');
            return;
        }

        this.subscriptions.add(
            this.store.dispatch(new UpdateVATOfYukiDocument(this.yukiDocument.uuid, this.createBodyToVerifyDocument())).subscribe({
                next: () => {
                    this.toastr.success('Yuki document successfully updated.');
                    this.emitDocumentVerified.emit();
                },
                error: () => {
                    this.logger.error('Failed to update Yuki document.');
                    this.toastr.error('Oops, something went wrong, Please try again later...');
                },
            }),
        );
    }

    calculateVatAmount(): void {
        const vatAmount: number = this.VATForm.value.amount - this.VATForm.value.amountExcl;
        this.VATForm.get('vatAmount').setValue(`${vatAmount.toFixed(2)}`);
        this.calculateVat();
    }

    calculateAmountExcl() {
        const amountExcl: number = this.VATForm.value.amount - this.VATForm.value.vatAmount;
        this.VATForm.get('amountExcl').setValue(`${amountExcl.toFixed(2)}`);
        this.calculateVat();
    }

    openCreateContactModal() {
        this.modalService.open(PartnerAddModalComponent, { windowClass: 'modal-pane', animation: false });
    }

    private createVATForm() {
        const doc = this.yukiDocument;
        const fallbackVat = VAT.OTHER;
        const vatValue = doc?.vat ?? fallbackVat;
        const vatAmount = this.toNumber((doc as any)?.vatamount ?? (doc as any)?.vatAmount ?? 0);
        const amount = this.toNumber((doc as any)?.amount ?? 0);
        const amountExcl = this.toNumber((doc as any)?.amountExcl ?? 0);

        this.VATForm = this.fb.group({
            vat: [vatValue, Validators.required],
            vatAmount: [`${vatAmount.toFixed(2)}`, Validators.required],
            amount: [`${amount.toFixed(2)}`, Validators.required],
            amountExcl: [`${amountExcl.toFixed(2)}`, Validators.required],
            description: doc?.description ?? '',
            subject: doc?.subject ?? '',
            contactName: doc?.contactName ?? '',
            invoiceNumber: [doc?.invoiceNumber ?? '', invoiceNumberValidator(doc?.folderId ?? 0)],
            linkedContact: [doc?.linkedContact ?? '', Validators.required],
            documentDate: doc?.documentDate ?? '',
        });

        this.tryAutoSelectLinkedContact();
    }

    private toNumber(value: unknown): number {
        if (typeof value === 'number' && Number.isFinite(value)) {
            return value;
        }
        if (typeof value === 'string') {
            const normalized = value
                .replace(/\s/g, '')
                .replace(/\./g, '')
                .replace(',', '.')
                .replace(/[^0-9.-]/g, '');
            const parsed = Number(normalized);
            return Number.isFinite(parsed) ? parsed : 0;
        }
        const parsed = Number(value ?? 0);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    private seedVATs() {
        this.VATs = Object.values(VAT);
    }

    private createBodyToVerifyDocument(): YukiDocumentDTO {
        // Resolve the linked contact UUID - ng-autocomplete may set a Partner object or a string
        let linkedContactId: string | undefined;
        const linkedContact = this.VATForm.value.linkedContact;
        if (linkedContact?.uuid) {
            linkedContactId = linkedContact.uuid;
        } else if (typeof linkedContact === 'string' && linkedContact.trim()) {
            // User typed a name but didn't select from dropdown — try to find the partner
            const match = (this.partners || []).find((p) => this.normalizeText(p?.title) === this.normalizeText(linkedContact));
            linkedContactId = match?.uuid;
        }

        return {
            vatPercent: this.VATForm.value.vat?.value,
            vatAmount: this.VATForm.value.vatAmount,
            amount: this.VATForm.value.amount,
            amountExcl: this.VATForm.value.amountExcl,
            description: this.VATForm.value.description,
            subject: this.VATForm.value.subject,
            contactName: this.VATForm.value.contactName,
            invoiceNumber: this.VATForm.value.invoiceNumber,
            linkedContactId,
            documentDate: this.VATForm.value.documentDate,
        };
    }

    private calculateVat() {
        const percentage = roundNumber(this.VATForm.value.vatAmount / this.VATForm.value.amountExcl);
        if (VAT.ZERO.percent - 0.2 < percentage * 100 && VAT.ZERO.percent + 0.2 > percentage * 100) this.VATForm.get('vat').setValue(VAT.ZERO);
        else if (VAT.SIX.percent - 0.2 < percentage * 100 && VAT.SIX.percent + 0.2 > percentage * 100) this.VATForm.get('vat').setValue(VAT.SIX);
        else if (VAT.TWELVE.percent - 0.2 < percentage * 100 && VAT.TWELVE.percent + 0.2 > percentage * 100) this.VATForm.get('vat').setValue(VAT.TWELVE);
        else if (VAT.TWENTY_ONE.percent - 0.2 < percentage * 100 && VAT.TWENTY_ONE.percent + 0.2 > percentage * 100)
            this.VATForm.get('vat').setValue(VAT.TWENTY_ONE);
        else this.VATForm.get('vat').setValue(VAT.OTHER);
    }

    private fetchData() {
        this.subscriptions.add(
            this.partners$.subscribe((partners) => {
                if (partners === null) {
                    this.logger.debug('Fetching partners');
                    this.store.dispatch(new FetchPartners());
                    return;
                }
                this.partners = partners;
                this.tryAutoSelectLinkedContact();
            }),
        );

        this.subscriptions.add(
            this.mappedContacts$.subscribe((mappedContacts) => {
                if (mappedContacts === null) {
                    this.store.dispatch(new GetMappedContacts());
                    return;
                }
                this.mappedContacts = mappedContacts || [];
                this.tryAutoSelectLinkedContact();
            }),
        );

        // Initial subscription to current document
        this.resubscribeToDocument();
    }

    /** Re-subscribe to the store selector when the document UUID changes (e.g., Verify & Next) */
    private resubscribeToDocument(): void {
        const newUuid = this.yukiDocument?.uuid;
        if (newUuid === this.subscribedDocumentUuid) {
            return; // already watching this document
        }

        // Unsubscribe from old document
        if (this.documentSubscription) {
            this.documentSubscription.unsubscribe();
            this.documentSubscription = null;
        }

        this.subscribedDocumentUuid = newUuid || null;
        if (!newUuid) {
            return;
        }

        this.documentSubscription = this.store.select(YukiState.documentById(newUuid)).subscribe((document) => {
            if (!document) {
                return;
            }
            this.yukiDocument = document;
            this.lastAutoSelectedDocumentUuid = null;
            this.createVATForm();
            this.loadCompanyLogbookPrompt();
        });
        this.subscriptions.add(this.documentSubscription);
    }

    private tryAutoSelectLinkedContact(force = false): void {
        if (!this.VATForm || !Array.isArray(this.partners) || this.partners.length === 0) {
            return;
        }

        const linkedContactControl = this.VATForm.get('linkedContact');
        if (!linkedContactControl || (!force && linkedContactControl.dirty)) {
            return;
        }

        const currentDocumentUuid = this.yukiDocument?.uuid || null;
        if (!force && currentDocumentUuid && this.lastAutoSelectedDocumentUuid === currentDocumentUuid) {
            return;
        }

        const currentContact = linkedContactControl.value as Partner;
        const resolvedCurrentContact = this.resolvePartnerByUuid(currentContact?.uuid);

        const rawYukiContactName = this.VATForm.get('contactName')?.value || this.yukiDocument?.contactName;
        const normalizedYukiContactName = this.normalizeText(rawYukiContactName);

        let bestPartner: Partner | null = null;
        if (normalizedYukiContactName) {
            bestPartner = this.findBestPartnerFromMappedContacts(normalizedYukiContactName) || this.findBestPartnerFromPartnerTitle(normalizedYukiContactName);
        }

        // When forced (AI flow): only use the AI match, don't fall back to stale existing contact
        const finalPartner = bestPartner || (force ? null : resolvedCurrentContact);
        if (finalPartner && (!currentContact || currentContact?.uuid !== finalPartner.uuid)) {
            linkedContactControl.setValue(finalPartner, { emitEvent: force });
        } else if (force && !finalPartner) {
            // AI couldn't match — clear stale contact so user picks the right one
            linkedContactControl.setValue(null, { emitEvent: true });
        }

        if (currentDocumentUuid) {
            this.lastAutoSelectedDocumentUuid = currentDocumentUuid;
        }
    }

    private findBestPartnerFromMappedContacts(normalizedYukiContactName: string): Partner | null {
        if (!Array.isArray(this.mappedContacts) || this.mappedContacts.length === 0) {
            return null;
        }

        let bestScore = 0;
        let bestPartner: Partner | null = null;

        for (const mapper of this.mappedContacts) {
            const mappedYukiContact = this.normalizeText(mapper?.yukiContact);
            const mappedPartnerUuid = mapper?.resumoContact?.uuid;
            if (!mappedYukiContact || !mappedPartnerUuid) {
                continue;
            }

            const score = this.scoreContactMatch(normalizedYukiContactName, mappedYukiContact);
            if (score <= bestScore) {
                continue;
            }

            const partner = this.resolvePartnerByUuid(mappedPartnerUuid);
            if (!partner) {
                continue;
            }

            bestScore = score;
            bestPartner = partner;
        }

        return bestPartner;
    }

    private findBestPartnerFromPartnerTitle(normalizedYukiContactName: string): Partner | null {
        let bestScore = 0;
        let bestPartner: Partner | null = null;

        for (const partner of this.partners || []) {
            const normalizedPartnerTitle = this.normalizeText(partner?.title);
            if (!normalizedPartnerTitle) {
                continue;
            }

            const score = this.scoreContactMatch(normalizedYukiContactName, normalizedPartnerTitle);
            if (score > bestScore) {
                bestScore = score;
                bestPartner = partner;
            }
        }

        return bestPartner;
    }

    private scoreContactMatch(normalizedSource: string, normalizedCandidate: string): number {
        if (!normalizedSource || !normalizedCandidate || normalizedCandidate.length < 2) {
            return 0;
        }

        const sourceCompact = this.compactText(normalizedSource);
        const candidateCompact = this.compactText(normalizedCandidate);
        if (!candidateCompact || candidateCompact.length < 2) {
            return 0;
        }

        if (sourceCompact === candidateCompact) {
            return 1000 + candidateCompact.length;
        }

        const wholeWordRegex = new RegExp(`(^|[^a-z0-9])${this.escapeRegExp(normalizedCandidate)}([^a-z0-9]|$)`, 'i');
        if (wholeWordRegex.test(normalizedSource)) {
            return 850 + candidateCompact.length;
        }

        // Also check reverse: source contained in candidate (e.g., AI returns "DVV" but partner is "DVV Verzekeringen")
        const reverseWordRegex = new RegExp(`(^|[^a-z0-9])${this.escapeRegExp(normalizedSource)}([^a-z0-9]|$)`, 'i');
        if (reverseWordRegex.test(normalizedCandidate) && sourceCompact.length >= 3) {
            return 750 + sourceCompact.length;
        }

        if (sourceCompact.includes(candidateCompact) && candidateCompact.length >= 3) {
            return 650 + candidateCompact.length;
        }

        // Reverse substring: candidate contains source
        if (candidateCompact.includes(sourceCompact) && sourceCompact.length >= 3) {
            return 550 + sourceCompact.length;
        }

        return 0;
    }

    private resolvePartnerByUuid(uuid: string | undefined): Partner | null {
        if (!uuid) {
            return null;
        }
        return (this.partners || []).find((partner) => partner?.uuid === uuid) || null;
    }

    private normalizeText(value: unknown): string {
        if (value === null || value === undefined) {
            return '';
        }
        return String(value)
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .replace(/\s+/g, ' ')
            .toLowerCase();
    }

    private compactText(value: string): string {
        return this.normalizeText(value).replace(/[^a-z0-9]/g, '');
    }

    private escapeRegExp(value: string): string {
        return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    private async loadCompanyLogbookPrompt(): Promise<void> {
        try {
            if (!this.companyUuid) {
                this.companyLogbookPrompt = null;
                return;
            }
            const prompt = await this.aiService.fetchCompanyLogbookPrompt(this.companyUuid);
            this.companyLogbookPrompt = prompt;
        } catch (err) {
            this.companyLogbookPrompt = null;
            this.logger.debug('Failed to load company logbook prompt', err);
        }
    }

    // =========================
    // AI: PDF of IMAGE analyseren
    // =========================
    async onAiVatClick(): Promise<void> {
        if (this.aiLoading) return;
        this.aiLoading = true;
        this.toastr.info('AI is analysing the document...');
        try {
            const src = await this.resolveDocumentSrc(); // { src, kind }
            if (!src) {
                this.toastr.error('No document file found (pdf/image).');
                return;
            }

            let result: AIResult;
            if (src.kind === 'pdf') {
                result = await this.aiService.suggestVatFromPdfUrl(src.src);
            } else if (src.kind === 'image') {
                result = await this.aiService.suggestVatFromImageUrl(src.src);
            } else {
                this.toastr.error('Unsupported document type.');
                return;
            }

            // === Form invullen ===

            // 0) Invoice number (only use when present)
            if (typeof result.invoiceNumber === 'string' && result.invoiceNumber.trim().length > 0) {
                this.VATForm.get('invoiceNumber')?.setValue(result.invoiceNumber);
            }

            // 0b) Contact name — set and trigger auto-select of Resumo contact
            if (typeof result.contactName === 'string' && result.contactName.trim().length > 0) {
                this.VATForm.get('contactName')?.setValue(result.contactName.trim());
            }
            // Always try to auto-select the Resumo contact based on (AI or existing) contactName
            this.lastAutoSelectedDocumentUuid = null;
            this.tryAutoSelectLinkedContact(true);

            // 1) Totaalbedrag incl. BTW (amount) — set before VAT so amountExcl can be derived
            if (typeof result.amountIncl === 'number' && !Number.isNaN(result.amountIncl) && result.amountIncl !== 0) {
                this.VATForm.get('amount')?.setValue(Number(result.amountIncl).toFixed(2));
            }

            // 2) BTW-bedrag — match sign to total amount (credit notes have negative amounts)
            if (typeof result.vatAmount === 'number' && !Number.isNaN(result.vatAmount)) {
                let vatAmount = Number(result.vatAmount);
                const totalAmount = parseFloat(this.VATForm.value.amount);
                if (totalAmount < 0 && vatAmount > 0) {
                    vatAmount = -vatAmount;
                } else if (totalAmount > 0 && vatAmount < 0) {
                    vatAmount = -vatAmount;
                }
                this.VATForm.get('vatAmount')?.setValue(vatAmount.toFixed(2));
            }

            // 3) BTW-percentage -> zet juiste enum
            if (typeof result.vatPercent === 'number') {
                const mapped = this.mapPercentToVatEnum(result.vatPercent);
                if (mapped) {
                    this.VATForm.get('vat')?.setValue(mapped);
                } else {
                    this.VATForm.get('vat')?.setValue(VAT.OTHER);
                }
            } else {
                this.VATForm.get('vat')?.setValue(VAT.OTHER);
            }

            // 4) Houd AI-bedrag leidend → herbereken enkel amountExcl
            this.calculateAmountExcl();

            this.logger.debug('AI reason:', result.reason, 'confidence:', result.confidence);
            this.toastr.success('AI VAT suggestion applied.');
            // Try to create sublinks automatically when AI ran (only for sales with project links)
            try {
                await this.tryCreateSublinksAutomatically(src);
            } catch (e) {
                this.logger.debug('Automatic sublink creation skipped or failed', e);
            }
        } catch (err: any) {
            this.logger.error(err);
            this.toastr.error(err?.message || 'AI VAT detection failed.');
        } finally {
            this.aiLoading = false;
        }
    }

    private async tryCreateSublinksAutomatically(src: { src: string; kind: 'pdf' | 'image' } | null): Promise<void> {
        try {
            if (!src || !this.yukiDocument) return;
            // Only for sales (folderId 2 = Verkoop)
            if (this.yukiDocument.folderId !== 2) return;

            // Ensure we have linked objects for this file
            await this.store.dispatch(new GetAllObjectsLinkedToFile(this.yukiDocument.uuid));
            const linkedObjectsToFile = await firstValueFrom(this.store.selectOnce(GlobalFilesState.LinkedObjectsToFileWithId));
            const linkedObjects = linkedObjectsToFile?.linkedObjects || [];

            const projectLink = linkedObjects.find((lo: any) => (lo.objectType || '').toLowerCase() === 'project');
            if (!projectLink) return; // only when file is linked to a project

            // Ensure we have linkable objects (employees etc.)
            await this.store.dispatch(new GetAllObjectsToLink());
            const linkFiles = await firstValueFrom(this.store.selectOnce(GlobalFilesState.LinkFiles));

            // Ask AI to extract person/amount candidates
            let candidates: Array<{ name: string; amount: number }> = [];
            try {
                if (src.kind === 'pdf') candidates = await this.aiService.suggestSublinkCandidatesFromPdfUrl(src.src);
                else candidates = await this.aiService.suggestSublinkCandidatesFromImageUrl(src.src);
            } catch (e) {
                this.logger.warn('AI sublink extraction failed', e);
                candidates = [];
            }

            if (!candidates || candidates.length === 0) {
                // fallback: open modal that already contains timesheet prefill logic
                const modalRef = this.modalService.open(AddChildLinkModalComponent, {
                    windowClass: 'modal-prompt modal-prompt--min-content',
                    animation: false,
                });
                modalRef.componentInstance.link = projectLink;
                modalRef.componentInstance.file = this.yukiDocument;
                modalRef.result
                    .then(async () => {
                        // refresh linked objects after modal saved
                        await this.store.dispatch(new GetAllObjectsLinkedToFile(this.yukiDocument.uuid));
                        this.toastr.success('Sublinks created from timesheet prefill.');
                    })
                    .catch(() => {
                        // dismissed/cancelled - no action
                    });
                return;
            }

            // Aggregate by name
            const agg = new Map<string, number>();
            for (const c of candidates) {
                const n = (c.name || '').trim();
                if (!n) continue;
                agg.set(n, +((agg.get(n) || 0) + Number(c.amount)).toFixed(2));
            }

            const dtos: ChildInvoiceLinkDTO[] = [];
            for (const [name, amount] of agg.entries()) {
                // try match to employee linkFile with improved heuristics
                const nameLower = name.toLowerCase();
                const employees = (linkFiles || []).filter((lf: any) => (lf.type || '').toLowerCase() === 'employee');
                let match = employees.find((lf: any) => (lf.displayName || '').toLowerCase() === nameLower);
                if (!match) {
                    match = employees.find((lf: any) => (lf.displayName || '').toLowerCase().includes(nameLower));
                }
                if (!match) {
                    // try last name token match
                    const token = nameLower.split(' ').slice(-1)[0];
                    match = employees.find((lf: any) => (lf.displayName || '').toLowerCase().includes(token));
                }
                if (!match) {
                    // try first name token match
                    const first = nameLower.split(' ')[0];
                    match = employees.find((lf: any) => (lf.displayName || '').toLowerCase().includes(first));
                }

                if (!match) {
                    this.logger.debug(`No matching employee object for name '${name}' — skipping sublink creation.`);
                    continue;
                }

                const dto: ChildInvoiceLinkDTO = {
                    objectIdForKey: projectLink.objectId,
                    fileIdForKey: this.yukiDocument.uuid,
                    newObjectLinkId: match.uuid,
                    amount: amount,
                    type: 'employee',
                };
                dtos.push(dto);
            }

            if (dtos.length === 0) {
                this.logger.debug('AI found names but none could be matched to employees. Opening timesheet prefill.');
                const modalRef = this.modalService.open(AddChildLinkModalComponent, {
                    windowClass: 'modal-prompt modal-prompt--min-content',
                    animation: false,
                });
                modalRef.componentInstance.link = projectLink;
                modalRef.componentInstance.file = this.yukiDocument;
                return;
            }

            // Compute remaining amount for this project (price minus already linked child amounts)
            const alreadyLinked = (projectLink.childInvoiceLinks || []).reduce((s: number, c: any) => s + (c.amount || 0), 0);
            let remainingForProject = +(projectLink.price - alreadyLinked).toFixed(2);
            if (remainingForProject <= 0) {
                this.logger.debug('No remaining amount on project to assign sublinks.');
                this.toastr.info('No remaining amount on linked project to assign AI sublinks.');
                return;
            }

            // Sum proposed amounts
            const proposedTotal = +dtos.reduce((s, d) => s + (Number(d.amount) || 0), 0).toFixed(2);

            // If proposedTotal is larger than remaining, scale down proportionally
            if (proposedTotal > remainingForProject) {
                this.logger.debug(`Scaling AI-proposed amounts from ${proposedTotal} down to remaining ${remainingForProject}`);
                const factor = remainingForProject / proposedTotal;
                let acc = 0;
                for (let i = 0; i < dtos.length; i++) {
                    const raw = Number(dtos[i].amount || 0);
                    const scaled = Math.round(raw * factor * 100) / 100;
                    dtos[i].amount = scaled;
                    acc += scaled;
                }
                // Fix rounding by adjusting last element
                const diff = +(remainingForProject - acc).toFixed(2);
                if (Math.abs(diff) >= 0.01 && dtos.length > 0) {
                    dtos[dtos.length - 1].amount = +(Number(dtos[dtos.length - 1].amount || 0) + diff).toFixed(2);
                }
            }

            // Recompute final total
            const finalTotal = +dtos.reduce((s, d) => s + (Number(d.amount) || 0), 0).toFixed(2);

            // If totals are off by more than 2 cents, ask for confirmation via the confirm modal
            const tolerance = 0.02;
            if (Math.abs(finalTotal - remainingForProject) > tolerance) {
                this.logger.debug(`AI proposed total ${finalTotal} differs from remaining ${remainingForProject} — prompting user to confirm.`);
                const modalRef = this.modalService.open(AddChildLinkConfirmFromTimesheetsModalComponent, {
                    windowClass: 'modal-prompt modal-prompt--min-content',
                    animation: false,
                });
                modalRef.componentInstance.newChildLinks = dtos;
                modalRef.componentInstance.linkPrice = projectLink.price;
                modalRef.componentInstance.alreadyLinkedPrice = alreadyLinked;
                // after user confirms and modal saved, refresh linked objects
                modalRef.result
                    .then(async () => {
                        await this.store.dispatch(new GetAllObjectsLinkedToFile(this.yukiDocument.uuid));
                        this.toastr.success('Sublinks created from AI suggestions.');
                    })
                    .catch(() => {
                        // user cancelled
                    });
                return;
            }

            // All good — dispatch
            await firstValueFrom(this.store.dispatch(new AddSubLinkToLinkedObjectToFile(dtos)));
            this.toastr.success('Sublinks created from AI suggestions.');
        } catch (err: any) {
            this.logger.error('Failed to create sublinks automatically', err);
        }
    }

    /**
     * Geeft data-URL en type terug op basis van contentType + NGXS file:
     *  - PDF:  data:application/pdf;base64,<base64>
     *  - IMAGE: data:<contentType>;base64,<base64>
     */
    private async resolveDocumentSrc(): Promise<{ src: string; kind: 'pdf' | 'image' } | null> {
        if (!this.yukiDocument) return null;

        const ct = (this.yukiDocument.contentType || '').toLowerCase();
        const isPdf = ct.includes('pdf');
        const isImage = ct.includes('image');
        const expectedFileName = this.yukiDocument.fileName;

        // 1) Hebben we al de juiste file in state?
        if (this.isMatchingFile(this.currentFile, expectedFileName)) {
            const base64 = this.currentFile!.fileAsBase64;
            if (isPdf) return { src: this.buildPdfDataUrl(base64), kind: 'pdf' };
            if (isImage) return { src: this.buildImageDataUrl(base64, ct), kind: 'image' };
        }

        // 2) Probeer file te laden en wachten tot de juiste binnenkomt
        if (this.yukiDocument.uuid && (isPdf || isImage)) {
            this.logger.debug('Fetching file for YukiDocument (from update-information component)');
            this.store.dispatch(new FetchFile(this.yukiDocument.uuid));
            try {
                const matched = await firstValueFrom(
                    this.file$.pipe(
                        filter((f) => this.isMatchingFile(f, expectedFileName)),
                        timeout(8000),
                        catchError(() => {
                            throw new Error('Timed out waiting for file from store.');
                        }),
                    ),
                );
                const base64 = matched.fileAsBase64;
                if (isPdf) return { src: this.buildPdfDataUrl(base64), kind: 'pdf' };
                if (isImage) return { src: this.buildImageDataUrl(base64, ct), kind: 'image' };
            } catch (e) {
                this.logger.error(e);
                // fallthrough
            }
        }

        // 3) Als fallback: probeer URL-properties op het document
        const urlFallback = this.pickUrl(this.yukiDocument);
        if (urlFallback) {
            if (isPdf || urlFallback.toLowerCase().includes('.pdf') || urlFallback.startsWith('data:application/pdf')) {
                return { src: urlFallback, kind: 'pdf' };
            }
            if (isImage || urlFallback.startsWith('data:image') || /\.(png|jpe?g|webp|gif)(\?|$)/i.test(urlFallback)) {
                return { src: urlFallback, kind: 'image' };
            }
        }

        return null;
    }

    private isMatchingFile(file: YukiFile | null | undefined, expectedFileName?: string | null): file is YukiFile {
        return !!file && !!expectedFileName && file.fileName === expectedFileName;
    }

    private buildPdfDataUrl(base64: string): string {
        return `data:application/pdf;base64,${base64}`;
    }

    private buildImageDataUrl(base64: string, contentTypeLower: string): string {
        // contentTypeLower is bv. 'image/png' of 'image/jpeg'
        const ct = contentTypeLower.startsWith('image/') ? contentTypeLower : 'image/png';
        return `data:${ct};base64,${base64}`;
    }

    private pickUrl(doc?: YukiDocument | null): string | null {
        if (!doc) return null;
        const candidates = [(doc as any).pdfUrl, (doc as any).documentUrl, (doc as any).fileUrl, (doc as any).url].filter(Boolean) as string[];

        for (const c of candidates) {
            if (typeof c === 'string') return c;
        }
        return null;
    }

    private mapPercentToVatEnum(percent: number): VAT | null {
        switch (Math.round(percent)) {
            case 0:
                return VAT.ZERO;
            case 6:
                return VAT.SIX;
            case 12:
                return VAT.TWELVE;
            case 21:
                return VAT.TWENTY_ONE;
            default:
                return null;
        }
    }
}

export function invoiceNumberValidator(folderId: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => (!control.value && folderId === 2 ? { invoiceNumber: { value: control.value } } : null);
}
